import { Component, Inject, OnDestroy, ViewChild } from '@angular/core';
import { LabelUpdateFunctionParams } from 'src/app/components/label-marker/label-maker.dialog.component';
import { MatDialogRef, MAT_DIALOG_DATA} from '@angular/material/dialog';
import { LabelObject, JoiEvent } from 'globals/store/events.data';
import { JoiFile } from 'globals/store/files.model';
import { ManageWrapperComponent, TabGroup } from './manage-wrapper.component';
import { exhibitorDisplayTypes } from 'globals/store/exhibitors.data';
import { OnsiteActivity, activityTabs, ManageActivityData } from 'globals/store/onsite.data';
import { activityReselector, ActivityReselectorData } from 'src/app/store/reselectors/onsite.reselector';
import { JoiInputComponent } from 'src/app/components/joi-input/joi-input.component';
import { removeUniqueStringFromList } from '../../helpers/helpers';
import { TagPanelData, TagType } from 'globals/store/tags.data';
import { Wrapper } from 'src/app/components/wrapper.component';
import { TimeHelper } from 'globals/helpers/time.helper';
import { TitleIconComponent } from 'src/app/components/icon/title-icon.component';
import { CommonModule, DatePipe } from '@angular/common';
import { FileLinkComponent } from 'src/app/components/file-handler/file-link.component';
import { FileHandlerComponent } from 'src/app/components/file-handler/file-handler.component';
import { TagPanelComponent } from './tag-panel.component';
import { AssetsComponent } from '../assets/assets.component';
import { jsonCopyObject } from 'globals/helpers/helpers';

@Component({
    selector: 'manage-activity',
    templateUrl: './manage-activity.html',
    styleUrls: ['./manage-dialogs.scss', './manage-activity.scss'],
    standalone: true,
    providers: [TimeHelper],
    imports: [ManageWrapperComponent, CommonModule, TitleIconComponent, JoiInputComponent, DatePipe, FileLinkComponent, FileHandlerComponent, TagPanelComponent, AssetsComponent]
})
export class ManageActivityComponent extends Wrapper<ManageActivityComponent> implements OnDestroy {

  _file = 'manage-activity'
  labels;
  activity: OnsiteActivity
  dateChanged = false
  activityTabs = activityTabs
  event: JoiEvent
  labelMap: LabelObject
  tabGroups: TabGroup[] = [{ tabs: [
    { id: activityTabs.info, name: 'Activity info' },
    { id: activityTabs.assets, name: 'Activity Assets' },
    { id: activityTabs.tagging, name: 'Tagging' },
  ]}]
  selectedTab
  exhibitorDisplayTypes = exhibitorDisplayTypes
  tagData: TagPanelData

  @ViewChild('timeInput') timeInput: JoiInputComponent

  public labelUpdateFunction({ labelTemplate, labelsChanged, labelsToggled, labels }: LabelUpdateFunctionParams) {
    // Updates to labels on an event don't go through the label dialog template but rather the entire event changes dialog (and are only saved on "save changes" or what have you)  The label template can still change, though
    if (labelsToggled) {
      this.activity.labels = labels
      this.saveActivity()
    }
    if (labelsChanged) {
      this.eventService.updateEvent(this.eventService.eventId, { activity_labels: labelTemplate })
    }
  }

  constructor(
    public dialogRef: MatDialogRef<ManageActivityComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ManageActivityData,
    private timeHelper: TimeHelper,
  ) {
    super()

    this.subCallback(this.store.select(activityReselector, this.data.activityId), (activityData: ActivityReselectorData) => {
      if (!activityData) return
      const { activity } = activityData
      this.activity = jsonCopyObject(activity);
      this.tagData = activityData
    })
    this.subCallback(this.store.select('events', 'labelMaps', 'activity_labels'), labelMap => {
      this.labels = Object.values(labelMap)
      this.labelMap = labelMap
    })
    this.subProp(this.store.select('events', 'event'), 'event')
  }

  setTags({ tagType, ids }: { tagType: TagType, ids: string[] }) {
    switch (tagType) {
      case 'attendee':
        this.activity = { ...this.activity, attendeeIds: ids }
        break
      case 'workforce': 
        this.activity = { ...this.activity, tags: ids }
        break
      case 'performer':
        this.activity = { ...this.activity, performerIds: ids }
        break
      case 'sponsor':
        this.activity = { ...this.activity, sponsorIds: ids }
        break
      case 'exhibitor':
        this.activity = { ...this.activity, exhibitorIds: ids }
        break
      case 'resource':
        this.activity = { ...this.activity, resourceIds: ids }
        break
      default:
        console.log(`Missing case for type: ${tagType}`)
        return
    }
    this.saveActivity()
  }

  removeTag({ tagType, id }: { tagType: TagType, id: string }) {
    switch (tagType) {
      case 'attendee': 
        removeUniqueStringFromList(this.activity.attendeeIds, id)
        break
      case 'workforce': 
        removeUniqueStringFromList(this.activity.tags, id)
        break
      case 'performer':
        removeUniqueStringFromList(this.activity.performerIds, id)
        break  
      case 'sponsor':
        removeUniqueStringFromList(this.activity.sponsorIds, id)
        break  
      case 'exhibitor':
        removeUniqueStringFromList(this.activity.exhibitorIds, id)
        break     
      case 'resource':
        removeUniqueStringFromList(this.activity.resourceIds, id)
        break 
      default:
        console.log(`Missing case for type: ${tagType}`)
        return
    }
    this.saveActivity()
  }

  updateTitle(newTitle: string) {
    console.log({newTitle})
    this.activity = { ...this.activity, title: newTitle }
    this.saveActivity()
  }

  updateTime() {
    const parsedTime = this.timeHelper.parseTime(this.activity.time)
    this.activity.time = parsedTime
    this.saveActivity()
    setTimeout(() => this.timeInput.setModel(parsedTime))
  }

  changeTab(tab) {
    this.selectedTab = tab
  }

  exit() {
    this.dialogRef.close()
  }
  

  toggleCompleted() {
    this.activity.completed = !this.activity.completed
    this.saveActivity()
  }
  
  saveFiles(files: JoiFile[]) {
    this.activity.files = files
    this.saveActivity()
  }

  addAssetIds(ids: string[]) {
    this.activity.assetIds = [...(this.activity.assetIds || []), ...ids]
    this.saveActivity()
  }

  removeAssetId(idToRemove: string) {
    this.activity.assetIds = this.activity.assetIds.filter(id => id !== idToRemove)
    this.saveActivity()
  }

  saveActivity() {
    console.log("SAVING ? ", { activity: this.activity })
    this.activityService.modifyActivity(this.activity)
  }

  async delete() {
    console.log("DELETING?")
    this.activityService.deleteActivity(this.activity.id)
    this.dialogRef.close()
  }


}
